import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Anchor,
  Badge,
  Button,
  Checkbox,
  Divider,
  Image,
  Loader,
  TextInput
} from '@mantine/core';
import { Photo } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import AppText from '../../common/AppText';
import AppFlexbox from '../../common/AppFlexbox';
import PaginationList from '../../common/PaginationList';
import * as classes from '../../../styles/nestedStyles.module.css';
import { ECOM_STORE_STATUS_LIST } from '../../../config/constants';

const StoreCreationStoreSelectModal = ({
  isOpen,
  onClose,
  onSelect,
  showStoreLink
}) => {
  const [filterState, setFilterState] = useState({
    showDeleted: false,
    search: '',
    status: '',
    pageIndex: 1,
    loading: false
  });
  const { state, fetchAdminResourceOptions } = useContext(AdminContext);
  const { ecomStores } = state.ecomResourceOptions.value || { ecomStores: [] };
  const filterStatusOption = ECOM_STORE_STATUS_LIST.find(
    (opt) => opt.value === filterState.status
  );
  const filteredStores =
    ecomStores?.filter(
      (s) =>
        (!filterStatusOption || filterStatusOption.isSelected(s)) &&
        (!filterState.search ||
          s.name.toLowerCase().includes(filterState.search.toLowerCase())) &&
        (filterState.showDeleted || !s.deleted)
    ) || [];

  useEffect(() => {
    if (isOpen) {
      setFilterState({
        showDeleted: false,
        search: '',
        status: '',
        pageIndex: 1,
        loading: false
      });

      if (
        !state.ecomResourceOptions.value &&
        !state.ecomResourceOptions.loading
      ) {
        fetchAdminResourceOptions(null, triggerNotification);
      }
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      bodyProps={{ style: { padding: 0 } }}
      isOpen={isOpen}
      onClose={onClose}
      title="Select store to copy from"
      zIndex={2000}
    >
      <AppStack
        style={{
          gap: 0,
          minHeight: 400,
          overflow: 'auto',
          flex: 1
        }}
      >
        <AppStack style={{ padding: 5, gap: 5 }}>
          <TextInput
            onChange={(e) =>
              setFilterState({
                ...filterState,
                search: e.currentTarget.value,
                pageIndex: 1
              })
            }
            placeholder="Filter by name..."
            style={{ flex: 1 }}
            value={filterState.search}
          />
        </AppStack>
        <Divider />
        <AppFlexbox
          style={{
            gap: 5,
            padding: 8,
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f3f3f3'
          }}
        >
          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
            Stores{' '}
            {!state.ecomResourceOptions.loading
              ? `(${filteredStores.length})`
              : ''}
          </AppText>

          <Checkbox
            checked={filterState.showDeleted}
            label="Show deleted stores"
            onChange={() => {
              setFilterState({
                ...filterState,
                showDeleted: !filterState.showDeleted,
                pageIndex: 1
              });
            }}
            styles={{
              label: { fontWeight: 500, cursor: 'pointer' },
              input: { cursor: 'pointer' }
            }}
          />
        </AppFlexbox>
        <Divider />
        <AppStack
          style={{
            gap: 0,
            flex: 1
          }}
        >
          {state.ecomResourceOptions.loading ? (
            <AppStack
              style={{
                gap: 5,
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px 10px',
                flex: 1
              }}
            >
              <Loader color="dark" size={34} />
            </AppStack>
          ) : filteredStores.length === 0 ? (
            <AppStack
              style={{
                flex: 1,
                border: 'solid 1px #dee2e6',
                borderRadius: 10,
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px'
              }}
            >
              <AppText style={{ fontSize: 14, color: '#666' }}>
                No stores {ecomStores.length === 0 ? 'available' : 'found'}
              </AppText>
              {ecomStores.length > 0 && (
                <Button
                  color="dark"
                  onClick={() => {
                    setFilterState({
                      ...filterState,
                      search: '',
                      status: '',
                      pageIndex: 1
                    });
                  }}
                  type="button"
                  variant="light"
                >
                  Clear Search
                </Button>
              )}
            </AppStack>
          ) : (
            <AppStack style={{ paddingBottom: 10 }}>
              <PaginationList
                items={filteredStores
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((p) => {
                    const statusInfo = ECOM_STORE_STATUS_LIST.find((opt) =>
                      opt.isSelected(p)
                    ) || { label: 'Unknown', color: 'grey' };

                    return (
                      <AppFlexbox
                        key={p.pkEcomStore}
                        className={classes['hover-action-card']}
                        onClick={() => {
                          onSelect(p);
                        }}
                        style={{
                          padding: '10px 16px',
                          gap: 16,
                          alignItems: 'center',
                          borderBottom: 'solid 1px #dee2e6',
                          cursor: 'pointer'
                        }}
                      >
                        <AppStack
                          style={{
                            width: 40,
                            height: 40,
                            border: p.logoImageUrl
                              ? 'none'
                              : 'solid 1px #dee2e6',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {p.logoImageUrl ? (
                            <Image h={40} src={p.logoImageUrl} w={40} />
                          ) : (
                            <Photo color="#666" size={24} />
                          )}
                        </AppStack>
                        <AppStack style={{ gap: 3 }}>
                          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                            {p.name} (ID: {p.pkEcomStore})
                          </AppText>

                          <Badge color={statusInfo.color} size="sm">
                            {statusInfo.label}
                          </Badge>

                          {showStoreLink && (
                            <AppFlexbox>
                              <Anchor
                                component={Link}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                style={{ color: 'dodgerblue', fontSize: 14 }}
                                target="_blank"
                                to={`/store/${p.urlHandle}`}
                              >
                                View store
                              </Anchor>
                            </AppFlexbox>
                          )}
                        </AppStack>
                      </AppFlexbox>
                    );
                  })}
                itemsPerPage={10}
                noBorder
                onPageChange={(pageIndex) =>
                  setFilterState({
                    ...filterState,
                    pageIndex
                  })
                }
                pageIndex={filterState.pageIndex}
              />
            </AppStack>
          )}
        </AppStack>
      </AppStack>
    </ResponsiveModal>
  );
};

StoreCreationStoreSelectModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSelect: PropTypes.func,
  showStoreLink: PropTypes.bool
};

export default StoreCreationStoreSelectModal;
